import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth';
import { CalendarClock, Flame, UserCircle, UserX, type LucideIcon } from 'lucide-react';
import { UNASSIGNED, toggleInArray, type FilterState } from '../lib/task-filter';

interface QuickFiltersProps {
  filters: FilterState;
  onChange: (filters: FilterState) => void;
}

/** Priorities the "Urgent" quick filter toggles as a set. */
const URGENT_SET = ['urgent', 'high'];

export function QuickFilters({ filters, onChange }: QuickFiltersProps) {
  const myEmployeeId = useAuthStore((s) => s.user?.employee?.id);

  const myActive = !!myEmployeeId && filters.assigneeIds.includes(myEmployeeId);
  const unassignedActive = filters.assigneeIds.includes(UNASSIGNED);
  const urgentActive = URGENT_SET.every((p) => filters.priorities.includes(p));
  const overdueActive = filters.overdue;

  const pills: { key: string; label: string; icon: LucideIcon; active: boolean; onClick: () => void }[] = [
    ...(myEmployeeId
      ? [
          {
            key: 'mine',
            label: 'My tasks',
            icon: UserCircle,
            active: myActive,
            onClick: () =>
              onChange({ ...filters, assigneeIds: toggleInArray(filters.assigneeIds, myEmployeeId) }),
          },
        ]
      : []),
    {
      key: 'unassigned',
      label: 'Unassigned',
      icon: UserX,
      active: unassignedActive,
      onClick: () =>
        onChange({ ...filters, assigneeIds: toggleInArray(filters.assigneeIds, UNASSIGNED) }),
    },
    {
      key: 'urgent',
      label: 'Urgent',
      icon: Flame,
      active: urgentActive,
      onClick: () =>
        onChange({
          ...filters,
          priorities: urgentActive
            ? filters.priorities.filter((p) => !URGENT_SET.includes(p))
            : Array.from(new Set([...filters.priorities, ...URGENT_SET])),
        }),
    },
    {
      key: 'overdue',
      label: 'Overdue',
      icon: CalendarClock,
      active: overdueActive,
      onClick: () => onChange({ ...filters, overdue: !filters.overdue }),
    },
  ];

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {pills.map((pill) => {
        const Icon = pill.icon;
        return (
          <button
            key={pill.key}
            type="button"
            onClick={pill.onClick}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors cursor-pointer',
              pill.active
                ? 'bg-hygge/10 text-hygge border-hygge/30'
                : 'border-border text-muted-foreground hover:bg-accent hover:text-foreground',
            )}
            aria-pressed={pill.active}
          >
            <Icon className="h-3.5 w-3.5" />
            {pill.label}
          </button>
        );
      })}
    </div>
  );
}
